
import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { ArrowLeft, Copy, ExternalLink, CheckCircle, Loader } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';

const TransactionConfirmation: React.FC = () => {
  const { setCurrentScreen, getCurrentTransaction, updateTransactionStatus } = useApp();
  const { toast } = useToast();
  const transaction = getCurrentTransaction();
  const [countdown, setCountdown] = useState(900); // 15 minutes in seconds
  const [copied, setCopied] = useState(false);
  const [progressValue, setProgressValue] = useState(30);

  // Generate a fake wallet address based on the selected crypto
  const getWalletAddress = () => {
    if (!transaction) return '';
    
    switch (transaction.cryptoCurrency) {
      case 'USDT':
        return '0x89205A3A3b2A69De6Dbf7f01ED13B2108B2c43e7';
      case 'BNB':
        return 'bnb1u2h3k4l5m6n7b8v9c0x9z8y7w6v5u4t3s2r1';
      case 'SOL':
        return 'GsvVmLks3qMXG7LzNPnYVzKc9aVkBbf1UCP9vgV3XQQ1';
      case 'USDC':
        return 'USdC89205A3A3b2A69De6Dbf7f01ED13B2108B2c43';
      default:
        return '0x89205A3A3b2A69De6Dbf7f01ED13B2108B2c43e7';
    }
  };

  useEffect(() => {
    if (!transaction) {
      setCurrentScreen('dashboard');
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // For demo purposes, automatically confirm the transaction after 10 seconds
    const confirmationTimer = setTimeout(() => {
      if (transaction) {
        updateTransactionStatus(transaction.id, 'confirmed');
        toast({
          title: "Transaction confirmed",
          description: "Your QR code is now ready for ATM withdrawal",
        });
        setCurrentScreen('atm-qr');
      }
    }, 10000);
    
    // Slowly increase the progress value for visual indication
    const progressTimer = setInterval(() => {
      setProgressValue(prev => {
        if (prev >= 90) {
          clearInterval(progressTimer);
          return 90;
        }
        return prev + 5;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      clearTimeout(confirmationTimer);
      clearInterval(progressTimer);
    };
  }, [transaction, setCurrentScreen, updateTransactionStatus, toast]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(getWalletAddress());
    setCopied(true);
    toast({
      title: "Address copied",
      description: "The wallet address has been copied to your clipboard",
    });
    
    setTimeout(() => setCopied(false), 3000);
  };

  if (!transaction) {
    return null;
  }

  return (
    <div className="flex flex-col h-full pb-16">
      <div className="flex items-center mb-6 pt-4">
        <button 
          onClick={() => setCurrentScreen('dashboard')}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold ml-4">Complete Transaction</h1>
      </div>

      <div className="flex-1 px-4">
        <div className="mobile-card mb-6">
          <h2 className="text-lg font-bold mb-4">Send {transaction.cryptoAmount} {transaction.cryptoCurrency}</h2>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-600 mb-2">Send exactly this amount to the address below:</p>
            <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
              <p className="font-mono text-sm break-all">{getWalletAddress()}</p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleCopyAddress} 
                className="flex-1 btn-outline flex items-center justify-center text-sm py-2"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" /> Copy Address
                  </>
                )}
              </button>
              <button className="flex-1 btn-outline flex items-center justify-center text-sm py-2">
                <ExternalLink className="w-4 h-4 mr-1" /> View QR
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4">
            <div className="flex items-start">
              <div className="mr-3">
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-amber-800">Important</h4>
                <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                  <li>Send only {transaction.cryptoCurrency} to this address</li>
                  <li>Transaction expires in {formatTime(countdown)}</li>
                  <li>Minimum 1 network confirmation required</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium mb-2 flex items-center">
              Transaction Status 
              {transaction.status === 'pending' && (
                <Loader className="w-4 h-4 ml-2 animate-spin text-neobank-primary" />
              )}
            </h3>
            <div className="flex items-center">
              <Progress value={transaction.status === 'pending' ? progressValue : 100} className="h-2.5" />
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span>Waiting for payment</span>
              <span>Confirming</span>
              <span>Complete</span>
            </div>
          </div>
        </div>

        <div className="text-center text-sm text-gray-600">
          <p>After your transaction is confirmed, you'll receive a QR code to withdraw cash at an ATM.</p>
        </div>
      </div>
    </div>
  );
};

export default TransactionConfirmation;
